// features/Messaging/Conversation/Conversation.tsx
import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'

import * as spriteIcons from 'assets/svg/sprite'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import Report from 'components/UI/Modal/Report/Report'
import { PaymentModal } from 'components-ref/molecules'
import { Modal } from 'components-ref/molecules/modal/modal'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import CreateMessage from 'features/Messaging/CreateMessage/CreateMessage'
import InboxContainer from 'features/Messaging/InboxContainer/InboxContainer'
import Message from 'features/Messaging/Message/Message'
import { setChatScroller } from 'helpers/chatScroller'
import { useConversationActions } from 'helpers/hooks/useConversationActions'
import { useConversationMessages } from 'helpers/hooks/useConversationMessages'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { useChatService } from 'services/chat/chatService'
import { useAccessToken, useUserProfile } from 'store'
import { type IProgressInfo } from 'types/interfaces/ITypes'

import style from './_conversation.module.scss'

export const Conversation = () => {
  const { id } = useParams<{ id?: string }>()
  const location = useLocation()

  const locationState = location.state as {
    otherUserData?: any
    conversationId?: string
  } | null

  const preloadedConversationId =
    locationState?.conversationId ?? null

  const otherUserData = locationState?.otherUserData

  const [conversationId, setConversationId] =
    useState<string | null>(preloadedConversationId)

  const otherUserId = id ?? null

  const [fanActionsOpen, setFanActionsOpen] = useState(false)

  const { t } = useTranslation()
  const userData = useUserProfile()
  const token = useAccessToken()
  const modalData = useModalContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setFooterHidden, clearFooterHidden } = useHeaderLayout()

  const [searchText, setSearchText] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [composerFocused, setComposerFocused] = useState(false)

  const [messageId, setMessageId] = useState<string | null>(null)
  const [reply, setReply] = useState<any>(null)

  const [selectedFiles, setSelectedFiles] = useState<any[]>([])
  const [progressInfos, setProgressInfos] = useState<{
    val: IProgressInfo[]
  }>({ val: [] })

  const [purchaseMsgId, setPurchaseMsgId] = useState<string>()
  const [confirmBuyModalOpen, setConfirmBuyModalOpen] =
    useState<boolean>(false)

  const [selectedMessageBuyPrice, setSelectedMessageBuyPrice] =
    useState(0)

  const [paymentMethod, setPaymentMethod] = useState<{
    method: 'card' | 'wallet'
    cardId: string | undefined
  }>({
    method: 'wallet',
    cardId: undefined,
  })

  const [attachmentsOpen, setAttachmentsOpen] = useState(false)

  const [headerHidden, setHeaderHidden] = useState(
    () => window.innerWidth < 950
  )

  const messageRefs =
    useRef<Record<string, HTMLDivElement | null>>({})

  const chatContainerRef = useRef<HTMLDivElement | null>(null)
  const inboxRef = useRef<HTMLDivElement>(null)
  const virtuosoRef = useRef<VirtuosoHandle | null>(null)
  const wasAtBottomBeforeFanToggleRef = useRef(true)
  const scrollerElRef = useRef<HTMLElement | null>(null)

  const [initialIndex, setInitialIndex] =
    useState<number | null>(null)

  const showRestrictedAccountModal = useCallback(() => {
    modalData.addModal(
      'Restricted Account',
      <ConfirmModal
        body="Please contact support for any assistance."
        cancelText="Cancel"
        confirmText="Submit Ticket"
        cancelButtonWidth="13"
        confirmButtonWidth="16"
        cancelFn={() => {
          modalData.clearModal()
        }}
        confirmFn={() => {
          modalData.clearModal()
          navigate('/support/home')
        }}
      />
    )
  }, [modalData, navigate])

const {
  displayedList,
  isLoading,
  isFetched,
  isError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  realtimeEnabled,
} = useConversationMessages({
  conversationId,
  setConversationId,
  otherUserId,
  searchTerm,
  onRestrictedAccount: showRestrictedAccountModal
})

const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            virtuosoRef.current?.scrollToIndex({
                index: displayedList.length,
                align: 'end',
                behavior: 'smooth',
            })
        })
    })
}, [displayedList.length])


  useEffect(() => {
    setConversationId(preloadedConversationId)

    setSearchText('')
    setSearchTerm('')
    setInitialIndex(null)
    setMessageId(null)
    setReply(null)
    setSelectedFiles([])
    setProgressInfos({ val: [] })
    setAttachmentsOpen(false)
    setFanActionsOpen(false)

    messageRefs.current = {}
    scrollerElRef.current = null
    setChatScroller(null)
  }, [id, preloadedConversationId])


  const handleFanActionsOpenChange = useCallback((open: boolean) => {
    wasAtBottomBeforeFanToggleRef.current = atBottomRef.current
    setFanActionsOpen(open)
  }, [])
  
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    setFooterHidden(true, 'conversation')
    return () => clearFooterHidden('conversation')
  }, [setFooterHidden, clearFooterHidden])

  const scrollToMessage = useCallback((msgId: string) => {
    if (messageRefs.current[msgId] && chatContainerRef.current) {
      const messageElement = messageRefs.current[msgId]
      const chatContainer = chatContainerRef.current

      const messageTop = messageElement?.offsetTop
      const chatScrollTop = chatContainer.scrollTop
      const chatHeight = chatContainer.clientHeight

      if (typeof messageTop === 'number') {
        if (messageTop < chatScrollTop || messageTop > chatScrollTop + chatHeight) {
          messageElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }
  }, [])


  const messagesReady =
    Boolean(conversationId) &&
    isFetched &&
    !isError

    // DEBOUNCE SEARCH
  useEffect(() => {
    const t = window.setTimeout(() => setSearchTerm(searchText), 500)
    return () => window.clearTimeout(t)
  }, [searchText])



useEffect(() => {
  if (!isFetched || isLoading) return
  if (initialIndex != null) return

  const len = displayedList?.length ?? 0

  setInitialIndex(len > 0 ? len - 1 : 0)
}, [
  isFetched,
  isLoading,
  displayedList?.length,
  initialIndex,
])

  const submitFn = useCallback((val: any) => {
    const media: any[] = []

    for (let i = 0; i < val.length; i++) {
      if (val[i].message?.message) {
        val[i].message.message.photos.forEach((photo: any) =>
          media.push({
            ...photo,
            percentage: 100,
            previewUrl: photo.url,
            storageUrl: photo.url,
            fileName: `shared-photo-${photo.id}`,
            type: 'photo',
          })
        )

        val[i].message.message.videos.forEach((video: any) => {
          const vaultableVideo = val[i].vaultable?.videos?.find((v: any) => v.id === video.id)
          media.push({
            ...video,
            percentage: 100,
            previewUrl: video.url,
            storageUrl: video.url,
            fileName: `shared-photo-${video.id}`,
            type: 'video',
            thumbnails: vaultableVideo?.thumbs?.map((thumb: any) => thumb.src) || [],
            preview: vaultableVideo?.preview || false,
          })
        })
      }

      if (val[i].post) {
        val[i].post.photos.forEach((photo: any) =>
          media.push({
            ...photo,
            percentage: 100,
            previewUrl: photo.url,
            storageUrl: photo.url,
            fileName: `shared-photo-${photo.id}`,
            type: 'photo',
          })
        )

        val[i].post.videos.forEach((video: any) =>
          media.push({
            ...video,
            percentage: 100,
            previewUrl: video.url,
            storageUrl: video.url,
            fileName: `shared-photo-${video.id}`,
            type: 'video',
          })
        )
      }
    }

    const mergedMedia = ([] as any[]).concat.apply([], media)
    mergedMedia.forEach((item: any) => (item.percentage = 100))

    setProgressInfos((prev) => ({ val: [...prev.val, ...mergedMedia] }))
    setSelectedFiles((prev) => [...prev, ...mergedMedia])
  }, [])


  
  const actions = useConversationActions({
    t,
    id,
    userData,
    otherUserData,
    conversationId,
    setConversationId,
    queryClient,
    modalData,
    navigate,

    setPurchaseMsgId,
    setConfirmBuyModalOpen,
    setSelectedMessageBuyPrice,

    scrollToMessage,
    scrollToBottom,
    submitFn,
  })

  useChatService({
    token,
    conversationId,
    queryClient,
    enabled: realtimeEnabled,
    currentUserId: userData?.id,
  })
const atBottomRef = useRef(true)

const canMountVirtuoso =
  Boolean(conversationId) &&
  isFetched &&
  !isLoading &&
  !isError

const initialTopMostItemIndex =
  displayedList.length > 0
    ? {
        index: displayedList.length - 1,
        align: 'end' as const,
      }
    : undefined

  // SCROLL DOWN WHEN OPENING FAN ACTIONS
  useEffect(() => {
    if (!wasAtBottomBeforeFanToggleRef.current) return

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scroller = scrollerElRef.current

        if (scroller) {
          scroller.scrollTop = scroller.scrollHeight
          return
        }

        virtuosoRef.current?.scrollTo({
          top: Number.MAX_SAFE_INTEGER,
          behavior: 'auto',
        })
      })
    })
  }, [fanActionsOpen])

// PUSH UP CONTENT WHEN KEYBOARD APPEARS
useEffect(() => {
  if (!composerFocused) return

  const viewport = document.querySelector<HTMLMetaElement>(
    'meta[name="viewport"]'
  )

  if (!viewport) return

  const previousContent = viewport.getAttribute('content') ?? ''

  viewport.setAttribute(
    'content',
    'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, interactive-widget=resizes-content'
  )

  return () => {
    viewport.setAttribute('content', previousContent)
  }
}, [composerFocused])
useEffect(() => {
  if (window.innerWidth >= 380) return

  const visualViewport = window.visualViewport
  if (!visualViewport) return

  const initialViewportHeight = visualViewport.height

  const setKeyboardOpen = (open: boolean) => {
    document.body.classList.toggle('header-hidden', open)
  }

  const onViewportResize = () => {
    const keyboardOpen =
      initialViewportHeight - visualViewport.height > 120

    setKeyboardOpen(keyboardOpen)
  }

  visualViewport.addEventListener('resize', onViewportResize)

  return () => {
    visualViewport.removeEventListener('resize', onViewportResize)
    setKeyboardOpen(false)
  }
}, [])
useEffect(() => {
  document.body.classList.toggle('keyboard-active', composerFocused)

  return () => {
    document.body.classList.remove('keyboard-active')
  }
}, [composerFocused])


useEffect(() => {
    const handleToggleHeader = () => {
        if (window.innerWidth >= 950) {
            setHeaderHidden(false)
            return
        }

        setHeaderHidden(prev => !prev)
    }

    const handleResize = () => {
        if (window.innerWidth >= 950) {
            setHeaderHidden(false)
        }
    }

    window.addEventListener('toggle-chat-header-section', handleToggleHeader)
    window.addEventListener('resize', handleResize)

    handleResize()

    return () => {
        window.removeEventListener('toggle-chat-header-section', handleToggleHeader)
        window.removeEventListener('resize', handleResize)
    }
}, [])
  return (
    <WithHeaderSection
        customClass={`single-chat ${headerHidden ? 'single-chat--header-hidden' : ''}`}
      headerSection={
        <LayoutHeader
          type="search-with-buttons"
          searchValue={searchText}
          searchFn={(val: string) => setSearchText(val)}
          additionalProps={{ placeholder: t('searchConversation') }}
          clearFn={() => setSearchText('')}
          buttons={[
            {
              type: 'actions',
              color: 'white',
              overlay: true,
              disabled: !otherUserData,
              elements: actions.headerActionElements,
            },
            {
              type: 'cta',
              color: 'white',
              icon:
                userData?.role === 'model' ? (
                  <spriteIcons.IconCameraOutlineOnline />
                ) : (
                  <spriteIcons.IconActionDollar color="#2894FF" width="20" height="20" />
                ),
              action: actions.addTip,
            },
          ]}
        />
      }
    >
      <InboxContainer containerRef={inboxRef}>
        <div
          className={`${style.chatContainer} ${
              attachmentsOpen ? style.attachmentsOpen : ''
          } ${!fanActionsOpen ? style.fanActionsHidden : ''}`}
          ref={chatContainerRef}
        >
          {isError ? null : !canMountVirtuoso ? (
            <div className={style.conversationLoading}>
              <BeatLoader size={10} />
            </div>
          ) : (
            <Virtuoso
              key={`convo-${conversationId ?? 'none'}`}
              ref={virtuosoRef}
              defaultItemHeight={200}
              increaseViewportBy={{ top: 600, bottom: 600 }}
              scrollerRef={(el) => {
                setChatScroller(el)
                scrollerElRef.current = el && el !== window ? (el as HTMLElement) : null
              }}
              data={displayedList}
              startReached={() => {
                if (!isFetchingNextPage && hasNextPage) fetchNextPage()
              }}
              overscan={{ reverse: 1400, main: 200 }}
              className={style.virtuoso}
              computeItemKey={(index, item: any) => String(item?.message?.id ?? item?.id ?? index)}
              atBottomStateChange={(atBottom) => { atBottomRef.current = atBottom }}
              atBottomThreshold={80}
              alignToBottom
              followOutput={(isAtBottom) => (!!isAtBottom)}
              initialTopMostItemIndex={initialTopMostItemIndex}
              itemContent={(index, message) => {
                const arr = displayedList
                const senderId = message?.message?.sender_id ?? message?.sender_id ?? 0
                const sender = senderId === userData?.id ? userData : otherUserData

                const prev = arr[index - 1]
                const next = arr[index + 1]

                let diff = 0
                let nextDiff = 0
                let sameMinutes = false
                let sameHours = false
                let nextSameMinutes = false
                let nextSameHours = false

                const date = new Date(message.message.created_at)
                const prevDate = prev ? new Date(prev.message.created_at) : null
                const nextDate = next ? new Date(next.message.created_at) : null

                const showDate = nextDate
                  ? `${date.getFullYear()}.${date.getMonth()}.${date.getDate()}` !==
                    `${nextDate.getFullYear()}.${nextDate.getMonth()}.${nextDate.getDate()}`
                  : true

                if (prevDate) {
                  diff = prevDate.getTime() - date.getTime()
                  if (prevDate.getMinutes() === date.getMinutes()) sameMinutes = true
                  if (prevDate.getHours() === date.getHours()) sameHours = true
                }

                if (nextDate) {
                  nextDiff = date.getTime() - nextDate.getTime()
                  if (nextDate.getMinutes() === date.getMinutes()) nextSameMinutes = true
                  if (nextDate.getHours() === date.getHours()) nextSameHours = true
                }

                const nextId = next?.message?.sender_id ?? next?.sender_id ?? null
                const prevId = prev?.message?.sender_id ?? prev?.sender_id ?? null

                const prevRecieved =
                  senderId === prevId && diff < 60000 && sameMinutes && sameHours && !message.sending

                const nextRecieved =
                  senderId === nextId && nextDiff < 60000 && nextSameMinutes && nextSameHours && !message.sending

                const isNewestFirst =
                  arr.length >= 2
                    ? new Date(arr[0].message.created_at).getTime() >= new Date(arr[1].message.created_at).getTime()
                    : true

                const isLastMessage = isNewestFirst ? index === 0 : index === arr.length - 1
                const uniqueKey = message.id || message.message?.id || `msg-${index}`

                return (
                  <div
                    key={uniqueKey}
                    className={isLastMessage ? style.newestMessage : ''}
                    ref={(el) => {
                      if (el) messageRefs.current[message.message.id] = el
                    }}
                  >
                    <Message
                      otherUserData={otherUserData}
                      message={message.message}
                      recieved={!message?.message?.is_sender}
                      next_recieved={nextRecieved}
                      prev_recieved={prevRecieved}
                      sender={sender}
                      reactions={message.reactions}
                      seen={
                        Array.isArray(message?.seen) && otherUserData?.id != null
                          ? message.seen.includes(otherUserData.id)
                          : false
                      }
                      sending={!!message.sending}
                      setReply={setReply}
                      addReaction={actions.addReaction}
                      scrollerEl={scrollerElRef.current}
                      messageId={messageId}
                      setMessageId={setMessageId}
                      deleteMessage={(val: string) => {
                        modalData.addModal(
                          t('deleteMessage'),
                          <ConfirmModal body={t('onceYouDeleteAMessage')} confirmFn={() => actions.deleteMessage(val)} />
                        )
                      }}
                      reportMessage={(val: string) => {
                        modalData.addModal(
                          t('reportMessage'),
                          <Report
                            type='content'
                            confirmFn={(msg: string, reportType: number) => actions.reportMessage(val, msg, reportType)}
                            closeFn={() => modalData.clearModal()}
                          />
                        )
                      }}
                      purchaseMessage={(msgId: string) => actions.openPurchase(msgId, message.message.price as number)}
                      showDate={showDate}
                      isLastMessage={isLastMessage}
                    />
                  </div>
                )
              }}
            />
          )}

          {isFetchingNextPage && (
            <div className={style.conversation_loader_container}>
              <div className="loader" />
            </div>
          )}

          {confirmBuyModalOpen &&
            (() => {
              const noDefaultCard = userData?.default_card == null || String(userData?.default_card) === ''
              const noWalletDeposit = userData?.wallet_deposit == null || Number(userData?.wallet_deposit) <= 0
              const showAddCard = noDefaultCard && noWalletDeposit

              return (
                <Modal
                  maxWidth={40}
                  onClose={() => setConfirmBuyModalOpen(false)}
                  title={showAddCard ? 'Add A Card' : 'Select payment method'}
                  closeOnLayerClick={true}
                  isLayerTransparent={false}
                >
                  {showAddCard ? (
                    <ConfirmModal
                      customClass="confirmModal--add-card"
                      confirmText="Add Card"
                      body={
                        <div>
                          <p className="add-card-title-spacing">
                            You don&apos;t have a saved card or wallet funds. Please add a card to continue.
                          </p>
                        </div>
                      }
                      onCancel={() => setConfirmBuyModalOpen(false)}
                      confirmFn={() => {
                        navigate('/settings/general/wallet')
                        setConfirmBuyModalOpen(false)
                      }}
                    />
                  ) : (
                    <PaymentModal
                      purchaseCb={() => {
                        if (actions.purchaseMutation.isLoading) return

                        actions.purchaseMutation.mutate({
                          id: purchaseMsgId,
                          paymentMethod: paymentMethod?.method,
                          cardId: paymentMethod?.cardId,
                        })
                      }}
                      paymentMethod={
                        paymentMethod as {
                          method: 'card' | 'deposit'
                          cardId: string | undefined
                        }
                      }
                      setPaymentMethod={
                        setPaymentMethod as (method: {
                          method: 'card' | 'deposit'
                          cardId: string | undefined
                        }) => void
                      }
                      price={selectedMessageBuyPrice}
                    />
                  )}
                </Modal>
              )
            })()}
        </div>
      </InboxContainer>

      <CreateMessage
        inboxRef={inboxRef}
        submitFn={submitFn}
        progressInfos={progressInfos}
        setProgressInfos={setProgressInfos}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        disabled={Boolean(otherUserData?.deleted)}
        onGifClick={(val: any) => actions.sendGif(val)}
        sendMessage={(val: any) => actions.sendMessage(val)}
        setReply={setReply}
        reply={reply}
        hasGif={true}
        otherUserData={otherUserData}
        conversationId={conversationId}
        messagesReady={messagesReady}
        onAttachmentsOpenChange={setAttachmentsOpen}
        fanActionsOpen={fanActionsOpen}
        onFanActionsOpenChange={handleFanActionsOpenChange}
        onFocusIn={() => setComposerFocused(true)}
        onFocusOut={() => setComposerFocused(false)}
        sendingDisabled={isLoading || isError || !conversationId}
      />
    </WithHeaderSection>
  )
}
